import useMembershipValidationWorkflow from 'hooks/useWorkflow/membershipValidationWorkflow';
import usePrequalOfferPush from 'hooks/useWorkflow/prequalOfferPush';
import usePrequalPhoneLookupWorkflow from 'hooks/useWorkflow/prequalPhoneLookupWorkflow';
import usePrequalTokenLookupWorkflow from 'hooks/useWorkflow/prequalTokenLookupWorkflow';
import log from 'loglevel';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectTenant } from 'state/tenant';
import useBatchPrescreenWorkflow from './batchPrescreenWorkflow';
import useDspPushWorkflow from './dspPushWorkflow';
import useEnhancedCheckoutWorkflow from './enhancedCheckoutWorkflow';
import useFmcWorkflow from './fmcWorkflow';
import useIcDriverLicenseWorkflow from './icDriverLicenseWorkflow';
import usePrequalDriverLicenseWorkflow from './prequalDriverLicenseWorkflow';
import usePrescreenWorkflow from './prescreenWorkflow';
import { Step, Workflow } from './Workflow';
import useWorkflowHelpers from './workflowHelpers';

/**
 * Workflow configuration.
 *
 * Provide a Workflow object and related helpers.
 */
type WorkflowHook = () => {
  /**
   * The raw workflow object
   */
  workflow: Workflow;

  /**
   * List of step in the current workflow
   */
  steps: Step[];

  /**
   * The 0-indexed step number for the given step in the current workflow
   */
  stepNumber: (step: Step) => number;

  /**
   * The path for a given step. Must exist in the current workflow.
   */
  pathFor: (step: Step) => string;

  /**
   * Return the step after the given step
   */
  stepAfter: (step: Step) => Step;

  /**
   * Return the path for the step after the given step.
   * Throw error if there are no next path.
   */
  pathAfter: (step: Step) => string;

  hasStep: (step: Step) => boolean;

  /**
   * Path to go to when restarting the application
   */
  restartPath: string;

  /**
   * The path to go to when the app times out
   */
  timeoutRestartPath: string;
};

const useWorkflow: WorkflowHook = () => {
  const { overrideConfig } = useSelector(selectTenant);

  const getFmcWorkflow = useFmcWorkflow();
  const getPrescreenWorkflow = usePrescreenWorkflow();
  const getBatchPrescreenWorkflow = useBatchPrescreenWorkflow();
  const getDspPushWorkflow = useDspPushWorkflow();
  const getPrequalPhoneLookupWorkflow = usePrequalPhoneLookupWorkflow();
  const getPrequalTokenLookupWorkflow = usePrequalTokenLookupWorkflow();
  const getPrequalOfferPush = usePrequalOfferPush();
  const getEnhancedCheckoutWorkflow = useEnhancedCheckoutWorkflow();
  const getMembershipValidationWorkflow = useMembershipValidationWorkflow();
  const getIcDriverLicenseWorkflow = useIcDriverLicenseWorkflow();
  const getPrequalDriverLicenseWorkflow = usePrequalDriverLicenseWorkflow();
  const workflow: Workflow = useMemo(() => {
    if (overrideConfig.dspPush) {
      return getDspPushWorkflow();
    } else if (overrideConfig.membershipValidation) {
      return getMembershipValidationWorkflow();
    } else if (overrideConfig.prescreen) {
      return getPrescreenWorkflow();
    } else if (overrideConfig.batchPrescreen) {
      return getBatchPrescreenWorkflow();
    } else if (overrideConfig.prequalPhoneLookup) {
      return getPrequalPhoneLookupWorkflow();
    } else if (overrideConfig.prequalTokenLookup) {
      return getPrequalTokenLookupWorkflow();
    } else if (overrideConfig.prequalOfferPush) {
      return getPrequalOfferPush();
    } else if (overrideConfig.icDriverLicense) {
      return getIcDriverLicenseWorkflow();
    } else if (overrideConfig.prequalDriverLicense) {
      return getPrequalDriverLicenseWorkflow();
    } else if (
      overrideConfig.enhancedCheckoutPhoneLookup ||
      overrideConfig.enhancedCheckoutTokenLookup
    ) {
      return getEnhancedCheckoutWorkflow();
    } else if (overrideConfig.tokenLookup || overrideConfig.phoneLookup) {
      return getFmcWorkflow();
    } else {
      // This should not happen, but for sanity's sake...
      log.error('Unsupported workflow', overrideConfig);
      throw new Error('Unsupported workflow');
    }
  }, [
    overrideConfig,
    getDspPushWorkflow,
    getMembershipValidationWorkflow,
    getPrescreenWorkflow,
    getBatchPrescreenWorkflow,
    getPrequalPhoneLookupWorkflow,
    getPrequalTokenLookupWorkflow,
    getPrequalOfferPush,
    getIcDriverLicenseWorkflow,
    getPrequalDriverLicenseWorkflow,
    getEnhancedCheckoutWorkflow,
    getFmcWorkflow,
  ]);

  const workflowHelpers = useWorkflowHelpers(workflow);

  return {
    workflow,
    ...workflowHelpers,
  };
};

export default useWorkflow;
